import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Printer, RefreshCw, Eye, EyeOff, FileText, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import api from "../lib/api";
import { Spinner, SectionLabel, Pill } from "../components/Primitives";
import usePageMeta from "../hooks/usePageMeta";

export default function Worksheet() {
  const { slug } = useParams();
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);

  const fetchWorksheet = async (regenerate = false) => {
    try {
      const res = await api.post(`/lessons/${slug}/worksheet`, { regenerate });
      setWs(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate the worksheet. Please try again.");
    } finally {
      setLoading(false);
      setRegenerating(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    api.post(`/lessons/${slug}/worksheet`, { regenerate: false })
      .then((res) => setWs(res.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not generate the worksheet. Please try again."))
      .finally(() => setLoading(false));
    if (new URLSearchParams(window.location.search).get("src") === "qr") {
      api.post("/qr-scan", { kind: "worksheet", slug }).catch(() => {});
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [slug]);

  usePageMeta(ws ? { title: `Worksheet · ${ws.lesson_title}` } : { title: "Worksheet" });

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center">
        <div className="mx-auto mb-5 h-10 w-10 animate-spin rounded-full border-2 border-edge border-t-gold" />
        <h2 className="font-serif text-3xl text-bone">Building your worksheet…</h2>
        <p className="mt-2 text-sm text-muted">The AI tutor is turning this lesson into a ready-to-assign handout.</p>
      </div>
    );
  }

  if (!ws) return <div className="mx-auto max-w-7xl px-6 py-32 text-center text-muted">Worksheet unavailable.</div>;

  const classroomUrl = `https://classroom.google.com/share?url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent("Worksheet: " + ws.lesson_title)}&body=${encodeURIComponent(ws.overview || "")}`;

  return (
    <div className="mx-auto max-w-3xl px-6 py-12 md:px-12" id="lesson-printable" data-testid="worksheet-page">
      {/* Controls */}
      <div className="no-print">
        <Link to={`/lessons/${slug}`} className="mb-6 inline-flex items-center gap-2 text-sm text-gold hover:underline" data-testid="back-to-lesson">
          <ArrowLeft className="h-4 w-4" /> Back to lesson
        </Link>
        <div className="mb-8 flex flex-wrap items-center gap-2">
          <button onClick={() => window.print()} data-testid="print-worksheet-btn" className="inline-flex items-center gap-2 border border-gold bg-gold/10 px-3 py-2 text-xs font-medium text-gold hover:bg-gold hover:text-ink">
            <Printer className="h-4 w-4" /> Print / PDF
          </button>
          <button onClick={() => setShowAnswers((s) => !s)} data-testid="toggle-answers-btn" className="inline-flex items-center gap-2 border border-edge px-3 py-2 text-xs text-muted hover:border-gold hover:text-gold">
            {showAnswers ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showAnswers ? "Hide answer key" : "Show answer key"}
          </button>
          <a href={classroomUrl} target="_blank" rel="noopener noreferrer" data-testid="worksheet-classroom-btn" className="inline-flex items-center gap-2 border border-edge px-3 py-2 text-xs text-muted hover:border-gold hover:text-gold">
            <GraduationCap className="h-4 w-4" /> Assign to Classroom
          </a>
          <button onClick={() => { setRegenerating(true); fetchWorksheet(true); }} disabled={regenerating} data-testid="regenerate-worksheet-btn" className="inline-flex items-center gap-2 border border-edge px-3 py-2 text-xs text-muted hover:border-gold hover:text-gold">
            <RefreshCw className={`h-4 w-4 ${regenerating ? "animate-spin" : ""}`} /> {regenerating ? "Regenerating…" : "Regenerate"}
          </button>
        </div>
      </div>

      {/* Print header */}
      <div className="print-header hidden">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
          <div>
            <h1 className="font-serif text-3xl font-semibold">Student Worksheet · {ws.lesson_title}</h1>
            <p className="mt-1 text-sm uppercase tracking-wide">{ws.category} · {ws.reading_level} · Our African Narratives</p>
            <p className="mt-3 text-sm">Name: ____________________________   Date: __________________</p>
          </div>
          <div style={{ textAlign: "center", flexShrink: 0 }}>
            <QRCodeSVG value={`${window.location.origin}/lessons/${slug}/worksheet?src=qr`} size={84} fgColor="#0A0908" bgColor="#ffffff" />
            <p style={{ fontSize: 10, marginTop: 4, color: "#555" }}>Scan to open online</p>
          </div>
        </div>
      </div>

      {/* Screen header */}
      <div className="no-print">
        <SectionLabel>Student Worksheet</SectionLabel>
        <div className="flex items-center gap-2">
          <FileText className="h-6 w-6 text-gold" />
          <h1 className="font-serif text-4xl font-semibold text-bone">{ws.lesson_title}</h1>
        </div>
        <Pill tone="gold" className="mt-4">{ws.category}</Pill>
      </div>

      {ws.overview && (
        <p className="ws-text mt-8 font-serif text-xl leading-relaxed text-[#e8e3d8]">{ws.overview}</p>
      )}

      {/* Objectives */}
      <Section title="Learning Objectives">
        <ul className="space-y-2">
          {(ws.objectives || []).map((o, i) => (
            <li key={i} className="ws-text flex gap-3 text-[#cfc9bd]"><span className="text-gold">▸</span>{o}</li>
          ))}
        </ul>
      </Section>

      {/* Key terms */}
      <Section title="Key Terms">
        <dl className="space-y-3">
          {(ws.key_terms || []).map((t, i) => (
            <div key={i} className="ws-text border-l-2 border-gold/50 pl-4">
              <dt className="font-medium text-bone">{t.term}</dt>
              <dd className="text-sm text-[#cfc9bd]">{t.definition}</dd>
            </div>
          ))}
        </dl>
      </Section>

      {/* Discussion questions */}
      <Section title="Discussion Questions">
        <ol className="space-y-6">
          {(ws.discussion_questions || []).map((q, i) => (
            <li key={i} className="ws-text">
              <p className="text-[#e8e3d8]"><span className="mr-2 font-mono text-gold">{i + 1}.</span>{q}</p>
              <div className="answer-lines mt-2 hidden">
                <div className="border-b border-gray-300 py-3" />
                <div className="border-b border-gray-300 py-3" />
              </div>
            </li>
          ))}
        </ol>
      </Section>

      {/* Quiz */}
      <Section title="Quick Quiz">
        <ol className="space-y-6">
          {(ws.quiz || []).map((q, i) => (
            <li key={i} className="ws-text" data-testid={`ws-quiz-${i}`}>
              <p className="text-[#e8e3d8]"><span className="mr-2 font-mono text-gold">{i + 1}.</span>{q.question}</p>
              <ul className="mt-2 space-y-1 pl-7">
                {(q.options || []).map((opt, j) => {
                  const isCorrect = showAnswers && j === q.correct_index;
                  return (
                    <li key={j} className={`ws-text text-sm ${isCorrect ? "font-semibold text-gold" : "text-[#cfc9bd]"}`}>
                      {String.fromCharCode(65 + j)}. {opt} {isCorrect && <span className="ml-1">✓</span>}
                    </li>
                  );
                })}
              </ul>
              {showAnswers && q.explanation && (
                <p className="answer-key mt-2 pl-7 text-xs text-muted">Answer: {String.fromCharCode(65 + q.correct_index)} — {q.explanation}</p>
              )}
            </li>
          ))}
        </ol>
      </Section>

      <div className="print-footer hidden">
        <p className="mt-10 border-t pt-3 text-xs">Generated by Our African Narratives — Un-erased Black History · {window.location.origin}</p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="ws-heading mb-4 font-serif text-2xl text-bone">{title}</h2>
      {children}
    </section>
  );
}
